import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Swal from "sweetalert2";
import { useConsultantVisibleContext } from "../../features/consultant/screenContextAdapter";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { ArrowLeft, ClipboardList, Eye, EyeOff, Pause, Play, Plus, X } from "lucide-react";
import { Toast } from "../../helpers/alert";
import {
  createProjectTask,
  getProjectBranches,
  getProjectById,
  getProjectCompanies,
  getProjectDevices,
  getProjectUsers,
  pauseProject,
  resumeProject,
  updateProject,
  updateProjectTask,
} from "../../redux/apiCalls/projectsCall/projectsCall";
import {
  getUserOrgIds,
  isMaster,
  userMatchesSelectionByIds,
} from "../../helpers/healthsafetyreports.helpers";
import {
  canManageProjects,
  DEVICE_TYPES,
  deviceMatchesScope,
  EQUIPMENT_FUNCTIONS,
  formatCurrency,
  formatDate,
  formatProgress,
  formatStatusLabel,
  getBranchId,
  getBranchLabel,
  getCompanyId,
  getCompanyLabel,
  getDeviceLabel,
  getScopeFromProjectForm,
  getUserLabel,
  PARTICIPANT_RESPONSIBILITIES,
  PROJECT_STATUSES,
  TASK_STATUSES,
  TASK_TYPES,
  toDateInputValue,
  toNullableNumber,
  toSelectOptions,
} from "./projects.helpers";
import ProjectCalculationModelSection from "../../features/calculationModels/ProjectCalculationModelSection";
import ProjectResultsSection from "../../features/calculationModels/ProjectResultsSection";
import ProjectExecutiveSummary from "../../features/projectGantt/ProjectExecutiveSummary";
import ProjectResourcesSummary from "../../features/projectGantt/ProjectResourcesSummary";
import ProjectCompletionSection from "../../features/projectGantt/ProjectCompletionSection";
import ProjectCostCurve from "../../features/projectGantt/ProjectCostCurveChart";
import usePendingChanges, { confirmDiscardChanges } from "../../features/plan3d/usePendingChanges";
import "./ProjectDetail.css";

const ProjectGanttSection = React.lazy(() => import("../../features/projectGantt/ProjectGanttSection"));

const emptyTaskForm = {
  title: "",
  description: "",
  status: "pendiente",
  task_type: "actividad",
  assigned_to: "",
  start_date: "",
  due_date: "",
  completed_date: "",
  planned_hours: "",
  actual_hours: "",
  planned_cost: "",
  actual_cost: "",
  progress_percentage: 0,
  dependency_ids: [],
};

const normalizeTaskDependencies = (value) => {
  if (!Array.isArray(value)) return [];
  return value.map((item) => {
    const source = item && typeof item === "object" ? item : { task_id: item };
    return {
      task_id: String(source.task_id ?? source.id ?? ""),
      relation: source.relation || "finish_to_start",
      lag_days: Number.isFinite(Number(source.lag_days)) ? Math.max(0, Number(source.lag_days)) : 0,
    };
  });
};

const emptyParticipant = {
  user_id: "",
  role: "otro",
  responsibility: "",
  notes: "",
};

const emptyEquipment = {
  device_id: "",
  device_name: "",
  device_type: "",
  description: "",
  function_in_project: "",
};

const getDeviceId = (device) => device?.id_device ?? device?.id;
const getDeviceType = (device) =>
  device?.device_type ||
  device?.type ||
  device?.equipment_type ||
  device?.primary_group ||
  "";

const normalizeProjectForm = (project = {}) => ({
  title: project.title || "",
  description: project.description || "",
  status: project.status || "propuesto",
  progress_percentage: formatProgress(project.progress_percentage),
  company_id: project.company_id ? String(project.company_id) : "",
  branch_id: project.branch_id ? String(project.branch_id) : "",
  start_date: toDateInputValue(project.start_date),
  planned_end_date: toDateInputValue(project.planned_end_date),
  actual_end_date: toDateInputValue(project.actual_end_date),
  planned_investment_total: project.planned_investment_total ?? "",
  actual_investment_total: project.actual_investment_total ?? "",
  planned_saving: project.planned_saving ?? "",
  actual_saving: project.actual_saving ?? "",
});

const normalizeParticipant = (participant = {}) => ({
  user_id: participant.user_id ? String(participant.user_id) : "",
  role: participant.role || participant.user?.privilege || "otro",
  responsibility: participant.responsibility || "",
  notes: participant.notes || "",
});

const normalizeEquipment = (item = {}) => ({
  device_id: item.device_id ? String(item.device_id) : "",
  device_name: item.device_name || item.device?.name || "",
  device_type: item.device_type || getDeviceType(item.device) || "",
  description: item.description || "",
  function_in_project: item.function_in_project || "",
});

const normalizeTaskForm = (task = {}) => ({
  title: task.title || "",
  description: task.description || "",
  status: task.status || "pendiente",
  task_type: task.task_type || "actividad",
  assigned_to: task.assigned_to ? String(task.assigned_to) : "",
  start_date: toDateInputValue(task.start_date),
  due_date: toDateInputValue(task.due_date),
  completed_date: toDateInputValue(task.completed_date),
  planned_hours: task.planned_hours ?? "",
  actual_hours: task.actual_hours ?? "",
  planned_cost: task.planned_cost ?? "",
  actual_cost: task.actual_cost ?? "",
  progress_percentage: formatProgress(task.progress_percentage),
  dependency_ids: normalizeTaskDependencies(task.dependency_ids),
});

function TaskDependenciesEditor({ value, tasks, currentTaskId, onChange, prefix }) {
  const dependencies = normalizeTaskDependencies(value);
  const selectedIds = new Set(dependencies.map((item) => String(item.task_id)));
  const addDependency = () => onChange([
    ...dependencies,
    { task_id: "", relation: "finish_to_start", lag_days: 0 },
  ]);
  const updateDependency = (index, field, nextValue) => onChange(dependencies.map((item, itemIndex) => (
    itemIndex === index ? { ...item, [field]: nextValue } : item
  )));
  const removeDependency = (index) => onChange(dependencies.filter((_, itemIndex) => itemIndex !== index));

  return (
    <div className="projects-field projects-field-full project-task-dependencies">
      <div className="project-task-dependencies-header">
        <div>
          <strong>Relación con otras tareas</strong>
          <p>Indicá si esta tarea debe esperar a que otra comience o termine.</p>
        </div>
        <button type="button" className="projects-btn projects-btn-secondary" onClick={addDependency}>
          <Plus size={15} /> Agregar relación
        </button>
      </div>
      {dependencies.length === 0 ? (
        <p className="project-task-dependencies-empty">Puede comenzar sin depender de otra tarea.</p>
      ) : dependencies.map((dependency, index) => {
        const predecessor = tasks.find((task) => String(task.id) === String(dependency.task_id));
        return (
          <div className="project-task-dependency-row" key={`${prefix}-dependency-${index}`}>
            <label>
              <span>Tarea anterior</span>
              <select value={dependency.task_id} onChange={(event) => updateDependency(index, "task_id", event.target.value)}>
                <option value="">Seleccionar tarea</option>
                {tasks.filter((task) => (
                  Number(task.id) !== Number(currentTaskId)
                  && (!selectedIds.has(String(task.id)) || String(task.id) === String(dependency.task_id))
                )).map((task) => <option key={task.id} value={task.id}>{task.title}</option>)}
              </select>
            </label>
            <label>
              <span>Esta tarea comienza</span>
              <select value={dependency.relation} onChange={(event) => updateDependency(index, "relation", event.target.value)}>
                <option value="finish_to_start">cuando termina la anterior</option>
                <option value="start_to_start">cuando comienza la anterior</option>
              </select>
            </label>
            <label className="project-task-dependency-lag">
              <span>Esperar (días)</span>
              <input type="number" min="0" step="1" value={dependency.lag_days} onChange={(event) => updateDependency(index, "lag_days", Math.max(0, Number(event.target.value || 0)))} />
            </label>
            <button type="button" className="project-task-dependency-remove" onClick={() => removeDependency(index)} aria-label="Quitar relación"><X size={17} /></button>
            {predecessor ? (
              <p className="project-task-dependency-summary">
                Comenzará {dependency.relation === "start_to_start" ? "cuando comience" : "después de que termine"} <strong>{predecessor.title}</strong>{dependency.lag_days ? `, esperando ${dependency.lag_days} día(s)` : ""}.
              </p>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

function ProjectDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useSelector((state) => state.user);
  const token = currentUser?.accessToken;
  const authenticated = Boolean(token);
  const tokenRef = useRef(token);
  const loadedProjectIdRef = useRef(null);
  const canManage = canManageProjects(currentUser?.privilege);

  const [project, setProject] = useState(null);
  useConsultantVisibleContext({ status: project?.status, progress: project?.progress_percentage });
  const [tasks, setTasks] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [users, setUsers] = useState([]);
  const [devices, setDevices] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [showEditProject, setShowEditProject] = useState(false);
  const [showCreateTask, setShowCreateTask] = useState(false);
  const [editProjectData, setEditProjectData] = useState(normalizeProjectForm());
  const [participantsEdit, setParticipantsEdit] = useState([]);
  const [equipmentEdit, setEquipmentEdit] = useState([]);
  const [taskForm, setTaskForm] = useState(emptyTaskForm);
  const [taskEdits, setTaskEdits] = useState({});
  const [taskEditMode, setTaskEditMode] = useState({});
  const [savingProject, setSavingProject] = useState(false);
  const [creatingTask, setCreatingTask] = useState(false);
  const [savingTaskId, setSavingTaskId] = useState(null);
  const [taskListExpanded, setTaskListExpanded] = useState(false);
  const [calculationWorkspaceOpen, setCalculationWorkspaceOpen] = useState(false);
  const [activeProjectTab, setActiveProjectTab] = useState("summary");
  const [ganttHasPendingChanges, setGanttHasPendingChanges] = useState(false);
  const [resultsRefreshKey, setResultsRefreshKey] = useState(0);
  const [latestProjectResult, setLatestProjectResult] = useState(null);
  const [finalizingProject, setFinalizingProject] = useState(false);
  const [changingLifecycle, setChangingLifecycle] = useState(false);

  usePendingChanges(ganttHasPendingChanges);

  useEffect(() => { tokenRef.current = token; }, [token]);

  const loadProjectData = useCallback(async () => {
    if (!authenticated || !id) return;
    const isInitialProjectLoad = String(loadedProjectIdRef.current) !== String(id);
    if (isInitialProjectLoad) setLoading(true);
    try {
      const requestToken = tokenRef.current;
      const projectData = await getProjectById(id, requestToken);
      const taskData = Array.isArray(projectData?.tasks) ? projectData.tasks : [];
      loadedProjectIdRef.current = id;
      setProject(projectData);
      setTasks(taskData);
      setEditProjectData(normalizeProjectForm(projectData));
      setParticipantsEdit(
        Array.isArray(projectData?.participants)
          ? projectData.participants.map(normalizeParticipant)
          : [],
      );
      setEquipmentEdit(
        Array.isArray(projectData?.equipment)
          ? projectData.equipment.map(normalizeEquipment)
          : [],
      );
      setLoading(false);
      setTaskEdits(
        taskData.reduce((acc, task) => {
          acc[task.id] = normalizeTaskForm(task);
          return acc;
        }, {}),
      );
    } catch (error) {
      Toast.fire({ icon: "error", title: "No se pudo cargar el proyecto" });
    } finally {
      setLoading(false);
    }
  }, [authenticated, id]);

  const loadOptions = useCallback(async () => {
    if (!token || !canManage) return;
    setLoadingOptions(true);
    try {
      const [usersData, devicesData] = await Promise.allSettled([
          getProjectUsers(token),
          getProjectDevices(token),
        ]);

      if (usersData.status === "fulfilled") {
        setUsers(Array.isArray(usersData.value) ? usersData.value : []);
      }
      if (devicesData.status === "fulfilled") {
        setDevices(Array.isArray(devicesData.value) ? devicesData.value : []);
      }
    } finally {
      setLoadingOptions(false);
    }
  }, [canManage, token]);

  const loadScopeOptions = useCallback(async () => {
    if (!token) return;
    const [branchesData, companiesData] = await Promise.allSettled([
      getProjectBranches(token),
      getProjectCompanies(token),
    ]);
    if (branchesData.status === "fulfilled") {
      setBranches(Array.isArray(branchesData.value) ? branchesData.value : []);
    }
    if (companiesData.status === "fulfilled") {
      setCompanies(Array.isArray(companiesData.value) ? companiesData.value : []);
    }
  }, [token]);

  useEffect(() => {
    loadProjectData();
  }, [loadProjectData]);

  useEffect(() => {
    loadScopeOptions();
  }, [loadScopeOptions]);

  useEffect(() => {
    if (showEditProject || showCreateTask || Object.values(taskEditMode).some(Boolean)) {
      loadOptions();
    }
  }, [loadOptions, showCreateTask, showEditProject, taskEditMode]);

  useEffect(() => {
    if (!showEditProject || editProjectData.company_id || companies.length !== 1) return;
    setEditProjectData((prev) => ({
      ...prev,
      company_id: String(getCompanyId(companies[0])),
    }));
  }, [companies, editProjectData.company_id, showEditProject]);

  const selectedScope = useMemo(
    () => getScopeFromProjectForm(editProjectData),
    [editProjectData],
  );

  const branchOptions = useMemo(
    () =>
      branches.filter(
        (branch) =>
          editProjectData.company_id &&
          Number(branch.parent_organization) === Number(editProjectData.company_id),
      ),
    [branches, editProjectData.company_id],
  );

  const projectCompanyName = useMemo(() => {
    const company = companies.find(
      (item) => Number(getCompanyId(item)) === Number(project?.company_id),
    );
    return company ? getCompanyLabel(company) : "-";
  }, [companies, project?.company_id]);

  const projectBranchName = useMemo(() => {
    const branch = branches.find(
      (item) => Number(getBranchId(item)) === Number(project?.branch_id),
    );
    return branch ? getBranchLabel(branch) : "-";
  }, [branches, project?.branch_id]);

  const filteredUsers = useMemo(() => {
    if (!selectedScope.branch_fk) return [];
    return users.filter(
      (user) =>
        isMaster(user) ||
        userMatchesSelectionByIds(getUserOrgIds(user), selectedScope),
    );
  }, [selectedScope, users]);

  const filteredDevices = useMemo(
    () => devices.filter((device) => deviceMatchesScope(device, selectedScope)),
    [devices, selectedScope],
  );

  const taskAssigneeOptions = useMemo(() => {
    const participantIds = new Set(
      (project?.participants || [])
        .map((participant) => Number(participant.user_id))
        .filter(Number.isFinite),
    );

    return Array.from(participantIds).map((userId) => {
      const participant = project.participants.find(
        (item) => Number(item.user_id) === userId,
      );
      const loadedUser = users.find(
        (user) => Number(user.id_user || user.id) === userId,
      );
      return {
        id: userId,
        label: getUserLabel(participant?.user || loadedUser || { id_user: userId }),
      };
    });
  }, [project, users]);

  const deviceTypeOptions = useMemo(
    () =>
      toSelectOptions(
        DEVICE_TYPES,
        filteredDevices.map(getDeviceType).concat(
          equipmentEdit.map((item) => item.device_type),
        ),
      ),
    [equipmentEdit, filteredDevices],
  );

  const equipmentFunctionOptions = useMemo(
    () =>
      toSelectOptions(
        EQUIPMENT_FUNCTIONS,
        equipmentEdit.map((item) => item.function_in_project),
      ),
    [equipmentEdit],
  );

  const resetProjectEdit = () => {
    setEditProjectData(normalizeProjectForm(project));
    setParticipantsEdit(
      Array.isArray(project?.participants)
        ? project.participants.map(normalizeParticipant)
        : [],
    );
    setEquipmentEdit(
      Array.isArray(project?.equipment) ? project.equipment.map(normalizeEquipment) : [],
    );
    setShowEditProject(false);
  };

  const handleProjectFieldChange = (event) => {
    const { name, value } = event.target;
    setEditProjectData((prev) => {
      const next = { ...prev, [name]: value };
      if (name === "company_id") {
        next.branch_id = "";
        setParticipantsEdit([]);
        setEquipmentEdit([]);
      }
      if (name === "branch_id") {
        setParticipantsEdit([]);
        setEquipmentEdit([]);
      }
      return next;
    });
  };

  const handleParticipantChange = (index, field, value) => {
    setParticipantsEdit((prev) => {
      const participants = prev.slice();
      const nextParticipant = { ...participants[index], [field]: value };
      if (field === "user_id") {
        const selected = filteredUsers.find(
          (user) => String(user.id_user || user.id) === String(value),
        );
        nextParticipant.role = selected?.privilege || "otro";
      }
      participants[index] = nextParticipant;
      return participants;
    });
  };

  const handleEquipmentChange = (index, field, value) => {
    setEquipmentEdit((prev) => {
      const equipment = prev.slice();
      const nextItem = { ...equipment[index], [field]: value };
      if (field === "device_id") {
        const selected = filteredDevices.find(
          (device) => String(getDeviceId(device)) === String(value),
        );
        if (selected) {
          nextItem.device_name = selected.name || selected.device_name || "";
          nextItem.device_type = getDeviceType(selected) || nextItem.device_type || "";
        }
      }
      equipment[index] = nextItem;
      return equipment;
    });
  };

  const buildProjectPayload = (overrides = {}) => ({
    title: editProjectData.title.trim(),
    description: editProjectData.description || null,
    status: editProjectData.status,
    progress_percentage: formatProgress(editProjectData.progress_percentage),
    company_id: editProjectData.company_id ? Number(editProjectData.company_id) : null,
    branch_id: editProjectData.branch_id ? Number(editProjectData.branch_id) : null,
    start_date: editProjectData.start_date || null,
    planned_end_date: editProjectData.planned_end_date || null,
    actual_end_date: editProjectData.actual_end_date || null,
    planned_investment_total: toNullableNumber(
      editProjectData.planned_investment_total,
    ),
    actual_investment_total: toNullableNumber(
      editProjectData.actual_investment_total,
    ),
    planned_saving: toNullableNumber(editProjectData.planned_saving),
    actual_saving: toNullableNumber(editProjectData.actual_saving),
    participants: participantsEdit
      .filter((participant) => participant.user_id)
      .map((participant) => ({
        user_id: Number(participant.user_id),
        role: participant.role || "otro",
        responsibility: participant.responsibility || null,
        notes: participant.notes || null,
      })),
    equipment: equipmentEdit
      .filter(
        (item) =>
          item.device_id ||
          item.device_name ||
          item.device_type ||
          item.description ||
          item.function_in_project,
      )
      .map((item) => ({
        device_id: item.device_id ? Number(item.device_id) : null,
        device_name: item.device_name || null,
        device_type: item.device_type || null,
        description: item.description || null,
        function_in_project: item.function_in_project || null,
      })),
    ...overrides,
  });

  const applyUpdatedProject = (updated) => {
    setProject(updated);
    setEditProjectData(normalizeProjectForm(updated));
    setParticipantsEdit(
      Array.isArray(updated?.participants)
        ? updated.participants.map(normalizeParticipant)
        : [],
    );
    setEquipmentEdit(
      Array.isArray(updated?.equipment) ? updated.equipment.map(normalizeEquipment) : [],
    );
  };

  const applyLifecycleProject = (updated) => {
    applyUpdatedProject(updated);
    const updatedTasks = Array.isArray(updated?.tasks) ? updated.tasks : [];
    setTasks(updatedTasks);
    setTaskEdits(updatedTasks.reduce((acc, task) => {
      acc[task.id] = normalizeTaskForm(task);
      return acc;
    }, {}));
  };

  const handlePauseProject = async () => {
    if (!token || !project || changingLifecycle) return;
    if (ganttHasPendingChanges) {
      await Swal.fire({
        icon: "info",
        title: "Hay cambios de tareas sin guardar",
        text: "Guardá o cancelá esos cambios antes de pausar el proyecto.",
        confirmButtonColor: "#1abb9c",
      });
      return;
    }
    const confirmation = await Swal.fire({
      icon: "question",
      title: "¿Pausar el proyecto?",
      text: "Mientras esté pausado, el cronograma no seguirá generando nuevos vencimientos. Las fechas, avances y costos actuales no se modificarán.",
      showCancelButton: true,
      confirmButtonText: "Sí, pausar",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#d49a19",
    });
    if (!confirmation.isConfirmed) return;
    setChangingLifecycle(true);
    try {
      const updated = await pauseProject(project.id, token);
      applyLifecycleProject(updated);
      Toast.fire({ icon: "success", title: "Proyecto pausado" });
    } catch (error) {
      Toast.fire({ icon: "error", title: error?.response?.data?.message || "No se pudo pausar el proyecto" });
    } finally {
      setChangingLifecycle(false);
    }
  };

  const handleResumeProject = async () => {
    if (!token || !project || changingLifecycle) return;
    const affectedTasks = tasks.filter((task) => !["completada", "cancelada"].includes(task.status));
    const decision = await Swal.fire({
      icon: "question",
      title: "¿Cómo querés reanudar el proyecto?",
      html: `<p>Hay <strong>${affectedTasks.length}</strong> tarea(s) activas o pendientes.</p><p>Podés correr sus fechas por el tiempo que duró la pausa o conservar el cronograma actual.</p>`,
      showCancelButton: true,
      showDenyButton: true,
      confirmButtonText: "Reprogramar tareas",
      denyButtonText: "Mantener fechas",
      cancelButtonText: "Seguir pausado",
      confirmButtonColor: "#1abb9c",
      denyButtonColor: "#52677a",
    });
    if (!decision.isConfirmed && !decision.isDenied) return;
    setChangingLifecycle(true);
    try {
      const scheduleAction = decision.isConfirmed ? "shift" : "keep";
      const updated = await resumeProject(project.id, scheduleAction, token);
      applyLifecycleProject(updated);
      Toast.fire({
        icon: "success",
        title: scheduleAction === "shift"
          ? `Proyecto reanudado · fechas corridas ${updated.pause_duration_days || 0} día(s)`
          : "Proyecto reanudado sin cambiar fechas",
      });
    } catch (error) {
      Toast.fire({ icon: "error", title: error?.response?.data?.message || "No se pudo reanudar el proyecto" });
    } finally {
      setChangingLifecycle(false);
    }
  };

  const updateTaskWithDependencyConfirmation = async (taskId, payload) => {
    try {
      return await updateProjectTask(project.id, taskId, payload, token);
    } catch (error) {
      const details = error?.response?.data?.details;
      if (error?.response?.status !== 409) {
        throw error;
      }
      if (details?.code === "TASK_DEPENDENCY_SCHEDULE_CONFLICT") {
        const confirmation = await Swal.fire({
          icon: "question",
          title: "Hay que ajustar la fecha de esta tarea",
          html: `<p>Por la relación configurada, debería comenzar el <strong>${formatDate(details.suggested_start_date)}</strong>.</p><p>¿Querés ajustar sus fechas automáticamente manteniendo la duración?</p>`,
          showCancelButton: true,
          confirmButtonText: "Ajustar fechas",
          cancelButtonText: "Volver a revisar",
          confirmButtonColor: "#1abb9c",
        });
        if (!confirmation.isConfirmed) return null;
        return updateTaskWithDependencyConfirmation(taskId, {
          ...payload,
          dependency_schedule_action: "shift",
        });
      }
      if (details?.code === "TASK_DEPENDENTS_NEED_RESCHEDULE") {
        const names = (details.tasks || []).map((item) => item.title).filter(Boolean);
        const confirmation = await Swal.fire({
          icon: "question",
          title: "Este cambio afecta tareas posteriores",
          html: `<p>${names.length ? `<strong>${names.join(" · ")}</strong>` : "Las tareas relacionadas"} quedarían antes de la fecha permitida.</p><p>¿Querés reprogramarlas automáticamente manteniendo su duración?</p>`,
          showCancelButton: true,
          showDenyButton: true,
          confirmButtonText: "Reprogramar relacionadas",
          denyButtonText: "Mover sólo esta tarea",
          cancelButtonText: "Cancelar",
          confirmButtonColor: "#1abb9c",
          denyButtonColor: "#d49a19",
        });
        if (!confirmation.isConfirmed && !confirmation.isDenied) return null;
        return updateTaskWithDependencyConfirmation(taskId, {
          ...payload,
          propagate_dependents: confirmation.isConfirmed,
          dependency_propagation_override: confirmation.isDenied,
        });
      }
      if (details?.code !== "TASK_DEPENDENCIES_INCOMPLETE") throw error;
      const names = (details.dependencies || []).map((item) => item.title).filter(Boolean);
      const confirmation = await Swal.fire({
        icon: "warning",
        title: "Hay una tarea relacionada pendiente",
        html: names.length
          ? `<p>La relación configurada todavía no se cumplió en:</p><p><strong>${names.join(" · ")}</strong></p><p>¿Querés continuar de todos modos?</p>`
          : "Esta tarea tiene una relación anterior todavía pendiente. ¿Querés continuar de todos modos?",
        showCancelButton: true,
        confirmButtonText: "Continuar de todos modos",
        cancelButtonText: "Volver a revisar",
        confirmButtonColor: "#d49a19",
      });
      if (!confirmation.isConfirmed) return null;
      return updateTaskWithDependencyConfirmation(taskId, {
        ...payload,
        dependency_override: true,
      });
    }
  };

  const showTaskUpdateError = async (error) => {
    const response = error?.response?.data;
    if (response?.details?.code === "TASK_DEPENDENCY_CYCLE") {
      await Swal.fire({
        icon: "warning",
        title: "Esta relación no se puede guardar",
        text: "Generaría un ciclo: las tareas terminarían dependiendo entre sí. Elegí otra tarea anterior o quitá una de las relaciones existentes.",
        confirmButtonText: "Entendido",
        confirmButtonColor: "#1abb9c",
      });
      return;
    }
    Toast.fire({
      icon: "error",
      title: response?.message || "No se pudo actualizar la tarea",
    });
  };

  const createTaskWithScheduleConfirmation = async (payload) => {
    try {
      return await createProjectTask(project.id, payload, token);
    } catch (error) {
      const details = error?.response?.data?.details;
      if (error?.response?.status !== 409 || details?.code !== "TASK_DEPENDENCY_SCHEDULE_CONFLICT") {
        throw error;
      }
      const confirmation = await Swal.fire({
        icon: "question",
        title: "Hay que ajustar la fecha de esta tarea",
        html: `<p>Por la relación configurada, debería comenzar el <strong>${formatDate(details.suggested_start_date)}</strong>.</p><p>¿Querés ajustar sus fechas automáticamente manteniendo la duración?</p>`,
        showCancelButton: true,
        confirmButtonText: "Ajustar fechas",
        cancelButtonText: "Volver a revisar",
        confirmButtonColor: "#1abb9c",
      });
      if (!confirmation.isConfirmed) return null;
      return createProjectTask(project.id, {
        ...payload,
        dependency_schedule_action: "shift",
      }, token);
    }
  };

  const mergeTaskResponse = (currentTasks, response) => {
    const changes = [response, ...(response?.rescheduled_tasks || [])]
      .filter(Boolean)
      .map((task) => [Number(task.id), task]);
    const byId = new Map(changes);
    return currentTasks.map((task) => byId.get(Number(task.id)) || task);
  };

  const applyTaskProgressToProject = (taskResponse) => {
    const nextProgress = Number(taskResponse?.project_progress_percentage);
    if (!Number.isFinite(nextProgress)) return;
    setProject((current) => (
      current ? { ...current, progress_percentage: nextProgress } : current
    ));
    setEditProjectData((current) => ({
      ...current,
      progress_percentage: nextProgress,
    }));
  };

  const handleSaveProject = async (event) => {
    event.preventDefault();
    if (!token || !project || !editProjectData.title.trim()) return;

    const isPausing = project.status === "en_ejecucion" && editProjectData.status === "pausado";
    const isResuming = project.status === "pausado" && editProjectData.status === "en_ejecucion";
    let scheduleAction = null;

    if (isPausing) {
      if (ganttHasPendingChanges) {
        await Swal.fire({
          icon: "info",
          title: "Hay cambios de tareas sin guardar",
          text: "Guardá o cancelá esos cambios antes de pausar el proyecto.",
          confirmButtonColor: "#1abb9c",
        });
        return;
      }
      const confirmation = await Swal.fire({
        icon: "question",
        title: "¿Guardar y pausar el proyecto?",
        text: "El proyecto quedará en pausa y el cronograma no generará nuevos vencimientos. Las fechas, avances y costos actuales no se modificarán.",
        showCancelButton: true,
        confirmButtonText: "Guardar y pausar",
        cancelButtonText: "Volver a editar",
        confirmButtonColor: "#d49a19",
      });
      if (!confirmation.isConfirmed) return;
    }

    if (isResuming) {
      const affectedTasks = tasks.filter((task) => !["completada", "cancelada"].includes(task.status));
      const decision = await Swal.fire({
        icon: "question",
        title: "¿Cómo querés reanudar el proyecto?",
        html: `<p>Hay <strong>${affectedTasks.length}</strong> tarea(s) activas o pendientes.</p><p>Podés correr sus fechas por el tiempo que duró la pausa o conservar el cronograma actual.</p>`,
        showCancelButton: true,
        showDenyButton: true,
        confirmButtonText: "Reprogramar tareas",
        denyButtonText: "Mantener fechas",
        cancelButtonText: "Seguir editando",
        confirmButtonColor: "#1abb9c",
        denyButtonColor: "#52677a",
      });
      if (!decision.isConfirmed && !decision.isDenied) return;
      scheduleAction = decision.isConfirmed ? "shift" : "keep";
    }

    setSavingProject(true);
    try {
      const lifecycleSafeStatus = isPausing || isResuming ? project.status : editProjectData.status;
      await updateProject(project.id, buildProjectPayload({ status: lifecycleSafeStatus }), token);
      if (isPausing) {
        const updated = await pauseProject(project.id, token);
        applyLifecycleProject(updated);
        Toast.fire({ icon: "success", title: "Proyecto actualizado y pausado" });
      } else if (isResuming) {
        const updated = await resumeProject(project.id, scheduleAction, token);
        applyLifecycleProject(updated);
        Toast.fire({
          icon: "success",
          title: scheduleAction === "shift"
            ? `Proyecto reanudado · fechas corridas ${updated.pause_duration_days || 0} día(s)`
            : "Proyecto reanudado sin cambiar fechas",
        });
      } else {
        const updated = await getProjectById(project.id, token);
        applyUpdatedProject(updated);
        Toast.fire({ icon: "success", title: "Proyecto actualizado" });
      }
      setShowEditProject(false);
    } catch (error) {
      Toast.fire({ icon: "error", title: error?.response?.data?.message || "No se pudo actualizar el proyecto" });
    } finally {
      setSavingProject(false);
    }
  };

  const handleTaskFormChange = (event) => {
    const { name, value } = event.target;
    setTaskForm((prev) => ({ ...prev, [name]: value }));
  };

  const buildTaskPayload = (form) => ({
    title: form.title.trim(),
    description: form.description || null,
    status: form.status,
    task_type: form.task_type || null,
    assigned_to: form.assigned_to ? Number(form.assigned_to) : null,
    start_date: form.start_date || null,
    due_date: form.due_date || null,
    completed_date: form.completed_date || null,
    planned_hours: toNullableNumber(form.planned_hours),
    actual_hours: toNullableNumber(form.actual_hours),
    planned_cost: toNullableNumber(form.planned_cost),
    actual_cost: toNullableNumber(form.actual_cost),
    progress_percentage: formatProgress(form.progress_percentage),
    dependency_ids: normalizeTaskDependencies(form.dependency_ids)
      .filter((item) => item.task_id)
      .map((item) => ({
        task_id: Number(item.task_id),
        relation: item.relation,
        lag_days: Number(item.lag_days || 0),
      })),
  });

  const handleCreateTask = async (event) => {
    event.preventDefault();
    if (!token || !project || !taskForm.title.trim()) return;

    setCreatingTask(true);
    try {
      const created = await createTaskWithScheduleConfirmation(buildTaskPayload(taskForm));
      if (!created) return;
      Toast.fire({ icon: "success", title: "Tarea creada" });
      setTasks((prev) => [...prev, created]);
      applyTaskProgressToProject(created);
      setTaskEdits((prev) => ({ ...prev, [created.id]: normalizeTaskForm(created) }));
      setTaskForm(emptyTaskForm);
      setShowCreateTask(false);
    } catch (error) {
      Toast.fire({ icon: "error", title: "No se pudo crear la tarea" });
    } finally {
      setCreatingTask(false);
    }
  };

  const handleTaskEditChange = (taskId, event) => {
    const { name, value } = event.target;
    setTaskEdits((prev) => ({
      ...prev,
      [taskId]: { ...(prev[taskId] || emptyTaskForm), [name]: value },
    }));
  };

  const handleToggleTaskEdit = (task) => {
    setTaskListExpanded(true);
    setTaskEdits((prev) => ({ ...prev, [task.id]: normalizeTaskForm(task) }));
    setTaskEditMode((prev) => ({ ...prev, [task.id]: true }));
  };

  const handleCancelTaskEdit = (task) => {
    setTaskEdits((prev) => ({ ...prev, [task.id]: normalizeTaskForm(task) }));
    setTaskEditMode((prev) => ({ ...prev, [task.id]: false }));
  };

  const handleSaveTask = async (taskId) => {
    if (!token || !project || !taskEdits[taskId]?.title?.trim()) return;

    setSavingTaskId(taskId);
    try {
      const updated = await updateTaskWithDependencyConfirmation(
        taskId,
        buildTaskPayload(taskEdits[taskId]),
      );
      if (!updated) return;
      Toast.fire({ icon: "success", title: "Tarea actualizada" });
      setTasks((prev) => mergeTaskResponse(prev, updated));
      applyTaskProgressToProject(updated);
      setTaskEdits((prev) => ({ ...prev, [taskId]: normalizeTaskForm(updated) }));
      setTaskEditMode((prev) => ({ ...prev, [taskId]: false }));
    } catch (error) {
      await showTaskUpdateError(error);
    } finally {
      setSavingTaskId(null);
    }
  };

  const handleCompleteProject = async (completionDate) => {
    if (!token || !project || !completionDate || finalizingProject) return;
    const confirmation = await Swal.fire({
      icon: "question",
      title: "¿Finalizar el proyecto?",
      text: "Se registrará la fecha real de finalización, el avance en 100 % y el estado cerrado.",
      showCancelButton: true,
      confirmButtonText: "Sí, finalizar",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#1abb9c",
    });
    if (!confirmation.isConfirmed) return;

    setFinalizingProject(true);
    try {
      const updated = await updateProject(
        project.id,
        {
          status: "cerrado",
          progress_percentage: 100,
          actual_end_date: completionDate,
        },
        token,
      );
      applyUpdatedProject(updated);
      Toast.fire({ icon: "success", title: "Proyecto finalizado" });
    } catch (error) {
      Toast.fire({ icon: "error", title: "No se pudo finalizar el proyecto" });
    } finally {
      setFinalizingProject(false);
    }
  };

  const handleGanttTaskUpdate = async (taskId, changes, options = {}) => {
    if (!token || !project) return null;
    const currentTask = tasks.find((task) => Number(task.id) === Number(taskId));
    if (!currentTask) return null;

    setSavingTaskId(taskId);
    try {
      const nextForm = normalizeTaskForm({ ...currentTask, ...changes });
      const updated = await updateTaskWithDependencyConfirmation(
        taskId,
        buildTaskPayload(nextForm),
      );
      if (!updated) return null;
      setTasks((prev) => mergeTaskResponse(prev, updated));
      applyTaskProgressToProject(updated);
      setTaskEdits((prev) => ({ ...prev, [taskId]: normalizeTaskForm(updated) }));
      if (!options.silent) Toast.fire({ icon: "success", title: "Tarea actualizada" });
      return updated;
    } catch (error) {
      if (!options.silent) await showTaskUpdateError(error);
      return null;
    } finally {
      setSavingTaskId(null);
    }
  };

  const renderTaskForm = (form, onChange, prefix = "task", currentTaskId = null, onDependenciesChange = null) => (
    <div className="projects-form-grid project-task-inline-form">
      <label className="projects-field projects-field-full">
        <span>Titulo *</span>
        <input name="title" value={form.title} onChange={onChange} required />
      </label>
      <label className="projects-field projects-field-full">
        <span>Descripcion</span>
        <textarea name="description" value={form.description} onChange={onChange} rows={2} />
      </label>
      <label className="projects-field">
        <span>Estado</span>
        <select name="status" value={form.status} onChange={onChange}>
          {TASK_STATUSES.map((status) => (
            <option key={`${prefix}-${status}`} value={status}>
              {formatStatusLabel(status)}
            </option>
          ))}
        </select>
      </label>
      <label className="projects-field">
        <span>Tipo</span>
        <select name="task_type" value={form.task_type} onChange={onChange}>
          {TASK_TYPES.map((type) => (
            <option key={`${prefix}-${type}`} value={type}>
              {formatStatusLabel(type)}
            </option>
          ))}
        </select>
      </label>
      <label className="projects-field">
        <span>Asignado a</span>
        <select name="assigned_to" value={form.assigned_to} onChange={onChange}>
          <option value="">Sin asignar</option>
          {taskAssigneeOptions.map((user) => (
            <option key={`${prefix}-${user.id}`} value={user.id}>
              {user.label}
            </option>
          ))}
        </select>
      </label>
      <label className="projects-field">
        <span>Avance %</span>
        <input
          type="number"
          name="progress_percentage"
          min="0"
          max="100"
          step="0.01"
          value={form.progress_percentage}
          onChange={onChange}
        />
      </label>
      <label className="projects-field">
        <span>Inicio</span>
        <input type="date" name="start_date" value={form.start_date} onChange={onChange} />
      </label>
      <label className="projects-field">
        <span>Vencimiento</span>
        <input type="date" name="due_date" value={form.due_date} onChange={onChange} />
      </label>
      <label className="projects-field">
        <span>Completada</span>
        <input type="date" name="completed_date" value={form.completed_date} onChange={onChange} />
      </label>
      <label className="projects-field">
        <span>Horas plan.</span>
        <input type="number" name="planned_hours" min="0" step="0.01" value={form.planned_hours} onChange={onChange} />
      </label>
      <label className="projects-field">
        <span>Horas reales</span>
        <input type="number" name="actual_hours" min="0" step="0.01" value={form.actual_hours} onChange={onChange} />
      </label>
      <label className="projects-field">
        <span>Costo plan.</span>
        <input type="number" name="planned_cost" min="0" step="0.01" value={form.planned_cost} onChange={onChange} />
      </label>
      <label className="projects-field">
        <span>Costo real</span>
        <input type="number" name="actual_cost" min="0" step="0.01" value={form.actual_cost} onChange={onChange} />
      </label>
      <TaskDependenciesEditor
        value={form.dependency_ids}
        tasks={tasks}
        currentTaskId={currentTaskId}
        prefix={prefix}
        onChange={onDependenciesChange || (() => {})}
      />
    </div>
  );

  const getAssigneeLabel = (userId, assignee) => {
    if (!userId) return "-";
    const option = taskAssigneeOptions.find((item) => Number(item.id) === Number(userId));
    return option?.label || getUserLabel(assignee || { id_user: userId });
  };

  const handleProjectTabChange = async (nextTab) => {
    if (nextTab === activeProjectTab) return;
    if (activeProjectTab === "action" && ganttHasPendingChanges) {
      const confirmed = await confirmDiscardChanges({
        title: "Hay cambios en el cronograma sin guardar",
        text: "Si cambiás de sección, se descartarán los ajustes realizados en las tareas.",
        confirmButtonText: "Descartar y cambiar de sección",
      });
      if (!confirmed) return;
      setGanttHasPendingChanges(false);
    }
    if (nextTab === "action") {
      setTaskListExpanded(false);
      setShowCreateTask(false);
    }
    setActiveProjectTab(nextTab);
  };

  if (loading) {
    return (
      <div className="project-detail-page">
        <p className="projects-empty">Cargando proyecto...</p>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="project-detail-page">
        <p className="projects-empty">Proyecto no encontrado.</p>
      </div>
    );
  }

  const progress = formatProgress(project.progress_percentage);
  return (
    <>
      <div className="cont-path">
        <p className="path path-cursor" onClick={() => navigate("/")}>
          Escritorio {">"}
        </p>
        <p className="path path-cursor" onClick={() => navigate("/improvement-projects")}>
          Proyectos {">"}
        </p>
        <p className="path">{project.title}</p>
      </div>

      <div className={`project-detail-page${calculationWorkspaceOpen ? " is-calculation-workspace" : ""}`}>
        <header className="project-detail-header">
          <div className="project-detail-heading-block">
            <button type="button" className="project-detail-back" onClick={() => navigate("/improvement-projects")}>
              <ArrowLeft size={14} /> Volver a proyectos
            </button>
            <div className="project-detail-title">
              <ClipboardList size={29} />
              <div>
                <h1>{project.title}</h1>
                <p>{project.description || "Sin descripción"}</p>
                {project.gantt_data?.source === "siger-recommendation" ? (
                  <span className="project-recommendation-origin">
                    Origen: Recomendación SIGER
                  </span>
                ) : null}
              </div>
            </div>
          </div>
          <div className="project-detail-actions">
            <span className={`projects-status-badge projects-status-${project.status}`}>
              {formatStatusLabel(project.status)}
            </span>
            {canManage && project.status === "en_ejecucion" && (
              <button type="button" className="projects-btn projects-btn-secondary" disabled={changingLifecycle || calculationWorkspaceOpen} onClick={handlePauseProject}>
                <Pause size={16} /> {changingLifecycle ? "Procesando..." : "Pausar proyecto"}
              </button>
            )}
            {canManage && project.status === "pausado" && (
              <button type="button" className="projects-btn projects-btn-primary" disabled={changingLifecycle || calculationWorkspaceOpen} onClick={handleResumeProject}>
                <Play size={16} /> {changingLifecycle ? "Procesando..." : "Reanudar proyecto"}
              </button>
            )}
          {canManage && (
            <button
              type="button"
              className="projects-btn projects-btn-primary"
              disabled={calculationWorkspaceOpen}
              onClick={() => {
                setActiveProjectTab("summary");
                if (showEditProject) resetProjectEdit();
                else setShowEditProject(true);
              }}
            >
              {showEditProject ? <X size={16} /> : null}
              {showEditProject ? "Cancelar edición" : "Editar proyecto"}
            </button>
          )}
          </div>
        </header>

        <nav className="project-detail-nav" aria-label="Secciones del proyecto">
          <button type="button" disabled={calculationWorkspaceOpen} className={activeProjectTab === "summary" ? "is-active" : ""} onClick={() => handleProjectTabChange("summary")}>Resumen</button>
          <button type="button" disabled={calculationWorkspaceOpen || showEditProject} className={activeProjectTab === "action" ? "is-active" : ""} onClick={() => handleProjectTabChange("action")}>Plan de acción</button>
          <button type="button" disabled={showEditProject} className={activeProjectTab === "monitoring" ? "is-active" : ""} onClick={() => handleProjectTabChange("monitoring")}>Seguimiento</button>
        </nav>

        {activeProjectTab === "summary" && <div className={`project-tab-panel${showEditProject ? " is-project-editing" : ""}`} role="tabpanel">
        {!showEditProject && <ProjectExecutiveSummary project={project} tasks={tasks} latestResult={latestProjectResult} />}
        <section className={`projects-card project-summary-card${showEditProject ? " is-editing" : ""}`}>
          <div className="projects-card-header projects-detail-section-header">
            <div>
              <h2>{showEditProject ? "Editar proyecto" : "Resumen"}</h2>
              <p>{showEditProject ? "Actualizá los datos generales, participantes y equipos" : "Vista general del proyecto"}</p>
            </div>
          </div>

          {!showEditProject ? (
            <div className="project-summary-grid">
              <div className="project-summary-item">
                <span>Avance</span>
                <strong>{progress}%</strong>
              </div>
              <div className="project-summary-item">
                <span>Compania</span>
                <strong>{projectCompanyName}</strong>
              </div>
              <div className="project-summary-item">
                <span>Sucursal</span>
                <strong>{projectBranchName}</strong>
              </div>
              <div className="project-summary-item">
                <span>Inicio</span>
                <strong>{formatDate(project.start_date)}</strong>
              </div>
              <div className="project-summary-item">
                <span>Fin planificado</span>
                <strong>{formatDate(project.planned_end_date)}</strong>
              </div>
              <div className="project-summary-item">
                <span>Fin real</span>
                <strong>{formatDate(project.actual_end_date)}</strong>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSaveProject} className="projects-detail-form">
              <div className="projects-card-header projects-inner-header">
                <h3>Editar datos generales</h3>
                <p>{loadingOptions ? "Cargando opciones..." : "Cambios guardados sobre el proyecto actual"}</p>
              </div>
              <div className="projects-form-grid">
                <label className="projects-field projects-field-full">
                  <span>Titulo *</span>
                  <input name="title" value={editProjectData.title} onChange={handleProjectFieldChange} required />
                </label>
                <label className="projects-field projects-field-full">
                  <span>Descripcion *</span>
                  <textarea name="description" value={editProjectData.description} onChange={handleProjectFieldChange} rows={3} required />
                </label>
                <label className="projects-field">
                  <span>Estado *</span>
                  <select name="status" value={editProjectData.status} onChange={handleProjectFieldChange}>
                    {PROJECT_STATUSES.filter((status) => (
                      project.status !== "pausado" || ["pausado", "en_ejecucion"].includes(status)
                    )).map((status) => (
                      <option
                        key={status}
                        value={status}
                        disabled={status === "pausado" && project.status !== "en_ejecucion" && project.status !== "pausado"}
                      >
                        {formatStatusLabel(status)}
                      </option>
                    ))}
                  </select>
                  {project.status === "pausado" ? <small>Para reanudarlo, elegí “En ejecución”. Al guardar podrás mantener o reprogramar las fechas.</small> : null}
                  {project.status === "en_ejecucion" ? <small>Si elegís “Pausado”, SIGER conservará el cronograma hasta que decidas reanudarlo.</small> : null}
                  {!["en_ejecucion", "pausado"].includes(project.status) ? <small>Un proyecto debe estar en ejecución antes de poder pausarlo.</small> : null}
                </label>
                <label className="projects-field">
                  <span>Avance % *</span>
                  <input type="number" name="progress_percentage" min="0" max="100" step="0.01" value={editProjectData.progress_percentage} onChange={handleProjectFieldChange} disabled={tasks.length > 0} />
                  {tasks.length > 0 ? <small>Calculado automáticamente a partir del avance de las tareas.</small> : <small>Podés informarlo manualmente mientras el proyecto no tenga tareas.</small>}
                </label>
                <label className="projects-field">
                  <span>Compania *</span>
                  <select name="company_id" value={editProjectData.company_id} onChange={handleProjectFieldChange} required>
                    <option value="">Seleccionar</option>
                    {companies.map((company) => {
                      const companyId = getCompanyId(company);
                      return (
                        <option key={companyId} value={companyId}>
                          {getCompanyLabel(company)}
                        </option>
                      );
                    })}
                  </select>
                </label>
                <label className="projects-field">
                  <span>Sucursal *</span>
                  <select name="branch_id" value={editProjectData.branch_id} onChange={handleProjectFieldChange} disabled={!editProjectData.company_id} required>
                    <option value="">Seleccionar</option>
                    {branchOptions.map((branch) => {
                      const branchId = getBranchId(branch);
                      return (
                        <option key={branchId} value={branchId}>
                          {getBranchLabel(branch)}
                        </option>
                      );
                    })}
                  </select>
                </label>
                <label className="projects-field">
                  <span>Fecha inicio *</span>
                  <input type="date" name="start_date" value={editProjectData.start_date} onChange={handleProjectFieldChange} />
                </label>
                <label className="projects-field">
                  <span>Fin planificado *</span>
                  <input type="date" name="planned_end_date" value={editProjectData.planned_end_date} onChange={handleProjectFieldChange} />
                </label>
                <label className="projects-field">
                  <span>Fin real</span>
                  <input type="date" name="actual_end_date" value={editProjectData.actual_end_date} onChange={handleProjectFieldChange} />
                </label>
                <label className="projects-field">
                  <span>Inversion planificada</span>
                  <input type="number" name="planned_investment_total" min="0" step="0.01" value={editProjectData.planned_investment_total} onChange={handleProjectFieldChange} />
                </label>
                <label className="projects-field">
                  <span>Inversion real</span>
                  <input type="number" name="actual_investment_total" min="0" step="0.01" value={editProjectData.actual_investment_total} onChange={handleProjectFieldChange} />
                </label>
                <label className="projects-field">
                  <span>Ahorro planificado</span>
                  <input type="number" name="planned_saving" min="0" step="0.01" value={editProjectData.planned_saving} onChange={handleProjectFieldChange} />
                </label>
                <label className="projects-field">
                  <span>Ahorro real</span>
                  <input type="number" name="actual_saving" min="0" step="0.01" value={editProjectData.actual_saving} onChange={handleProjectFieldChange} />
                </label>
              </div>

              <div className="projects-form-section">
                <div className="projects-section-title">
                  <h3>Participantes</h3>
                  <button type="button" className="projects-btn projects-btn-secondary" onClick={() => setParticipantsEdit((prev) => [...prev, { ...emptyParticipant }])}>
                    <Plus size={14} />
                    Agregar
                  </button>
                </div>
                {participantsEdit.length === 0 ? (
                  <p className="projects-inline-empty">Sin participantes cargados.</p>
                ) : (
                  <div className="projects-edit-list">
                    {participantsEdit.map((participant, index) => (
                      <div className="projects-edit-row" key={`participant-${index}`}>
                        <label className="projects-field">
                          <span>Usuario</span>
                          <select value={participant.user_id} onChange={(event) => handleParticipantChange(index, "user_id", event.target.value)}>
                            <option value="">Seleccionar</option>
                            {filteredUsers.map((user) => (
                              <option key={user.id_user || user.id} value={user.id_user || user.id}>
                                {getUserLabel(user)}
                              </option>
                            ))}
                          </select>
                        </label>
                        <label className="projects-field">
                          <span>Rol</span>
                          <input value={formatStatusLabel(participant.role)} disabled readOnly />
                        </label>
                        <label className="projects-field">
                          <span>Responsabilidad</span>
                          <select value={participant.responsibility} onChange={(event) => handleParticipantChange(index, "responsibility", event.target.value)}>
                            <option value="">Seleccionar</option>
                            {PARTICIPANT_RESPONSIBILITIES.map((responsibility) => (
                              <option key={responsibility} value={responsibility}>
                                {responsibility}
                              </option>
                            ))}
                          </select>
                        </label>
                        <label className="projects-field">
                          <span>Notas</span>
                          <input value={participant.notes} onChange={(event) => handleParticipantChange(index, "notes", event.target.value)} />
                        </label>
                        <button type="button" className="projects-btn projects-btn-danger" onClick={() => setParticipantsEdit((prev) => prev.filter((_, idx) => idx !== index))}>
                          Eliminar
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="projects-form-section">
                <div className="projects-section-title">
                  <h3>Equipos</h3>
                  <button type="button" className="projects-btn projects-btn-secondary" onClick={() => setEquipmentEdit((prev) => [...prev, { ...emptyEquipment }])}>
                    <Plus size={14} />
                    Agregar
                  </button>
                </div>
                {equipmentEdit.length === 0 ? (
                  <p className="projects-inline-empty">Sin equipos asociados.</p>
                ) : (
                  <div className="projects-edit-list">
                    {equipmentEdit.map((item, index) => (
                      <div className="projects-edit-row projects-edit-row-equipment" key={`equipment-${index}`}>
                        <label className="projects-field">
                          <span>Equipo existente</span>
                          <select value={item.device_id} onChange={(event) => handleEquipmentChange(index, "device_id", event.target.value)}>
                            <option value="">Sin vincular</option>
                            {filteredDevices.map((device) => {
                              const deviceId = getDeviceId(device);
                              return (
                                <option key={deviceId} value={deviceId}>
                                  {getDeviceLabel(device)}
                                </option>
                              );
                            })}
                          </select>
                        </label>
                        <label className="projects-field">
                          <span>Nombre</span>
                          <input value={item.device_name} onChange={(event) => handleEquipmentChange(index, "device_name", event.target.value)} />
                        </label>
                        <label className="projects-field">
                          <span>Tipo</span>
                          <select value={item.device_type} onChange={(event) => handleEquipmentChange(index, "device_type", event.target.value)}>
                            <option value="">Seleccionar</option>
                            {deviceTypeOptions.map((type) => (
                              <option key={type} value={type}>
                                {formatStatusLabel(type)}
                              </option>
                            ))}
                          </select>
                        </label>
                        <label className="projects-field">
                          <span>Funcion</span>
                          <select value={item.function_in_project} onChange={(event) => handleEquipmentChange(index, "function_in_project", event.target.value)}>
                            <option value="">Seleccionar</option>
                            {equipmentFunctionOptions.map((func) => (
                              <option key={func} value={func}>
                                {formatStatusLabel(func)}
                              </option>
                            ))}
                          </select>
                        </label>
                        <label className="projects-field projects-field-full">
                          <span>Descripcion</span>
                          <input value={item.description} onChange={(event) => handleEquipmentChange(index, "description", event.target.value)} />
                        </label>
                        <button type="button" className="projects-btn projects-btn-danger" onClick={() => setEquipmentEdit((prev) => prev.filter((_, idx) => idx !== index))}>
                          Eliminar
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="projects-form-actions">
                <button type="button" className="projects-btn projects-btn-secondary" onClick={resetProjectEdit}>
                  Cancelar
                </button>
                <button type="submit" className="projects-btn projects-btn-primary" disabled={savingProject}>
                  {savingProject ? "Guardando..." : "Guardar cambios"}
                </button>
              </div>
            </form>
          )}
        </section>

        {!showEditProject && <>
        <section className="projects-card">
          <div className="projects-card-header projects-detail-section-header">
            <div>
              <h2>Participantes</h2>
              <p>{project.participants?.length || 0} participante(s)</p>
            </div>
          </div>
          <div className="project-participants-list">
            {(project.participants || []).length === 0 ? (
              <p className="projects-inline-empty">Sin participantes.</p>
            ) : (
              project.participants.map((participant) => (
                <article className="project-list-item" key={participant.id || participant.user_id}>
                  <strong>{getUserLabel(participant.user || { id_user: participant.user_id })}</strong>
                  <span>{formatStatusLabel(participant.role)}</span>
                  <p>{participant.responsibility || "-"}</p>
                  {participant.notes ? <small>{participant.notes}</small> : null}
                </article>
              ))
            )}
          </div>
        </section>

        <section className="projects-card">
          <div className="projects-card-header projects-detail-section-header">
            <div>
              <h2>Equipos</h2>
              <p>{project.equipment?.length || 0} equipo(s)</p>
            </div>
          </div>
          <div className="project-equipment-list">
            {(project.equipment || []).length === 0 ? (
              <p className="projects-inline-empty">Sin equipos asociados.</p>
            ) : (
              project.equipment.map((item) => (
                <article className="project-list-item" key={item.id || item.device_id || item.device_name}>
                  <strong>{item.device_name || getDeviceLabel(item.device)}</strong>
                  <span>{formatStatusLabel(item.device_type)}</span>
                  <p>{formatStatusLabel(item.function_in_project)}</p>
                  {item.description ? <small>{item.description}</small> : null}
                </article>
              ))
            )}
          </div>
        </section>

        <ProjectCompletionSection
          project={project}
          tasks={tasks}
          canManage={canManage}
          saving={finalizingProject}
          onComplete={handleCompleteProject}
        />
        </>}

        </div>}

        {activeProjectTab === "action" && <div className="project-tab-panel" role="tabpanel">
        <React.Suspense fallback={<section className="projects-card"><p className="projects-empty">Cargando cronograma...</p></section>}>
          <ProjectGanttSection
            tasks={tasks}
            projectStatus={project.status}
            pausedAt={project.gantt_data?.lifecycle?.active_pause?.paused_at}
            assigneeOptions={taskAssigneeOptions}
            canEdit={canManage}
            updatingTaskId={savingTaskId}
            onTaskUpdate={handleGanttTaskUpdate}
            onPendingChange={setGanttHasPendingChanges}
            onEditTask={handleToggleTaskEdit}
          />
        </React.Suspense>
        <ProjectResourcesSummary tasks={tasks} assigneeOptions={taskAssigneeOptions} />
        <section className="projects-card">
          <div className="projects-card-header projects-detail-section-header">
            <div>
              <h2>Listado de tareas</h2>
              <p>{tasks.length} tarea(s)</p>
            </div>
            <div className="project-task-list-actions">
              <button type="button" className="projects-btn projects-btn-secondary" onClick={() => setTaskListExpanded(value => !value)}>
                {taskListExpanded ? <EyeOff size={14} /> : <Eye size={14} />}
                {taskListExpanded ? "Ocultar listado" : "Mostrar listado"}
              </button>
            {canManage && (
              <button type="button" className="projects-btn projects-btn-secondary" onClick={() => {
                setTaskListExpanded(true);
                setShowCreateTask((prev) => !prev);
              }}>
                {showCreateTask ? <X size={14} /> : <Plus size={14} />}
                {showCreateTask ? "Cerrar" : "Nueva tarea"}
              </button>
            )}
            </div>
          </div>

          {taskListExpanded && showCreateTask && canManage && (
            <form onSubmit={handleCreateTask} className="project-task-create">
              {renderTaskForm(
                taskForm,
                handleTaskFormChange,
                "create-task",
                null,
                (dependency_ids) => setTaskForm((current) => ({ ...current, dependency_ids })),
              )}
              <div className="projects-form-actions">
                <button type="button" className="projects-btn projects-btn-secondary" onClick={() => setShowCreateTask(false)}>
                  Cancelar
                </button>
                <button type="submit" className="projects-btn projects-btn-primary" disabled={creatingTask}>
                  {creatingTask ? "Guardando..." : "Guardar tarea"}
                </button>
              </div>
            </form>
          )}

          {taskListExpanded && (tasks.length === 0 ? (
            <p className="projects-empty">Sin tareas cargadas.</p>
          ) : (
            <>
            <div className="projects-table-wrapper">
              <table className="projects-table">
                <thead>
                  <tr>
                    <th>Titulo</th>
                    <th>Estado</th>
                    <th>Tipo</th>
                    <th>Asignado</th>
                    <th>Inicio</th>
                    <th>Vencimiento</th>
                    <th>Avance</th>
                    <th>Costos</th>
                    <th>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {tasks.map((task) => (
                    <React.Fragment key={task.id}>
                      <tr>
                        <td>
                          <div className="projects-project-name">{task.title}</div>
                          {task.description ? <p className="project-task-description">{task.description}</p> : null}
                        </td>
                        <td>
                          <span className={`projects-status-badge projects-status-${task.status}`}>
                            {formatStatusLabel(task.status)}
                          </span>
                        </td>
                        <td>{formatStatusLabel(task.task_type)}</td>
                        <td>{getAssigneeLabel(task.assigned_to, task.assignee)}</td>
                        <td>{formatDate(task.start_date)}</td>
                        <td>{formatDate(task.due_date)}</td>
                        <td>{formatProgress(task.progress_percentage)}%</td>
                        <td>{formatCurrency(task.actual_cost)} / {formatCurrency(task.planned_cost)}</td>
                        <td>
                          {canManage && (
                            <button type="button" className="projects-btn projects-btn-view" onClick={() => handleToggleTaskEdit(task)}>
                              Editar
                            </button>
                          )}
                        </td>
                      </tr>
                      {taskEditMode[task.id] ? (
                        <tr className="project-task-edit-row">
                          <td colSpan="9">
                            {renderTaskForm(
                              taskEdits[task.id] || normalizeTaskForm(task),
                              (event) => handleTaskEditChange(task.id, event),
                              `edit-task-${task.id}`,
                              task.id,
                              (dependency_ids) => setTaskEdits((current) => ({
                                ...current,
                                [task.id]: { ...(current[task.id] || normalizeTaskForm(task)), dependency_ids },
                              })),
                            )}
                            <div className="projects-form-actions">
                              <button type="button" className="projects-btn projects-btn-secondary" onClick={() => handleCancelTaskEdit(task)}>
                                Cancelar
                              </button>
                              <button type="button" className="projects-btn projects-btn-primary" disabled={savingTaskId === task.id} onClick={() => handleSaveTask(task.id)}>
                                {savingTaskId === task.id ? "Guardando..." : "Guardar tarea"}
                              </button>
                            </div>
                          </td>
                        </tr>
                      ) : null}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="project-task-mobile-list">
              {tasks.map((task) => (
                <article className="project-task-mobile-card" key={`mobile-${task.id}`}>
                  <div className="project-task-mobile-heading">
                    <div><strong>{task.title}</strong>{task.description ? <p>{task.description}</p> : null}</div>
                    <span className={`projects-status-badge projects-status-${task.status}`}>{formatStatusLabel(task.status)}</span>
                  </div>
                  <dl>
                    <div><dt>Período</dt><dd>{formatDate(task.start_date)} — {formatDate(task.due_date)}</dd></div>
                    <div><dt>Avance</dt><dd>{formatProgress(task.progress_percentage)} %</dd></div>
                    <div><dt>Responsable</dt><dd>{getAssigneeLabel(task.assigned_to, task.assignee)}</dd></div>
                    <div><dt>Tipo</dt><dd>{formatStatusLabel(task.task_type)}</dd></div>
                  </dl>
                  {canManage && <button type="button" className="projects-btn projects-btn-view" onClick={() => handleToggleTaskEdit(task)}>Editar tarea</button>}
                  {taskEditMode[task.id] ? <div className="project-task-mobile-edit">{renderTaskForm(taskEdits[task.id] || normalizeTaskForm(task), event => handleTaskEditChange(task.id, event), `mobile-edit-task-${task.id}`, task.id, (dependency_ids) => setTaskEdits((current) => ({ ...current, [task.id]: { ...(current[task.id] || normalizeTaskForm(task)), dependency_ids } })))}<div className="projects-form-actions"><button type="button" className="projects-btn projects-btn-secondary" onClick={() => handleCancelTaskEdit(task)}>Cancelar</button><button type="button" className="projects-btn projects-btn-primary" disabled={savingTaskId === task.id} onClick={() => handleSaveTask(task.id)}>{savingTaskId === task.id ? "Guardando..." : "Guardar tarea"}</button></div></div> : null}
                </article>
              ))}
            </div>
            </>
          ))}
        </section>
        </div>}

        <div
          className="project-results-preload-host"
          hidden={activeProjectTab !== "monitoring" || calculationWorkspaceOpen}
          aria-hidden={activeProjectTab !== "monitoring" || calculationWorkspaceOpen}
        >
          <ProjectResultsSection project={project} refreshKey={resultsRefreshKey} onResultChange={setLatestProjectResult} />
        </div>

        {activeProjectTab === "monitoring" && <div className={`project-tab-panel project-monitoring-panel${calculationWorkspaceOpen ? " is-workspace" : ""}`} role="tabpanel">
        {!calculationWorkspaceOpen && <section className="projects-card project-finance-section">
          <div className="projects-card-header">
            <h2>Indicadores económicos</h2>
            <p>Valores planificados y reales informados en el proyecto</p>
          </div>
          <div className="project-summary-grid">
            <div className="project-summary-item">
              <span>Inversion planificada</span>
              <strong>{formatCurrency(project.planned_investment_total)}</strong>
            </div>
            <div className="project-summary-item">
              <span>Inversion real</span>
              <strong>{formatCurrency(project.actual_investment_total)}</strong>
            </div>
            <div className="project-summary-item">
              <span>Ahorro planificado</span>
              <strong>{formatCurrency(project.planned_saving)}</strong>
            </div>
            <div className="project-summary-item">
              <span>Ahorro real</span>
              <strong>{formatCurrency(project.actual_saving)}</strong>
            </div>
          </div>
          <ProjectCostCurve tasks={tasks} />
        </section>}
        <ProjectCalculationModelSection project={project} onWorkspaceChange={setCalculationWorkspaceOpen} onConfigurationChange={() => setResultsRefreshKey(value => value + 1)} />
        </div>}
      </div>
    </>
  );
}

export default ProjectDetail;
